// BLEND MODE ALGORITMIT - Pelkkä matematiikka, ei CSS blend-modea
// Laske kahden värin yhdistelmä -> käytä pelkkää background-color:ia

export interface RgbColor {
  r: number
  g: number
  b: number
}

export type BlendMode =
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'soft-light'
  | 'hard-light'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'difference'
  | 'exclusion'
  | 'normal'

export function hexToRgb(hex: string): RgbColor | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null
}

export const rgbaToHex = (rgba: string): { hex: string; alpha: number } => {
  if (rgba.startsWith('#')) return { hex: rgba, alpha: 1 }

  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/)
  if (!match) return { hex: '#ffffff', alpha: 1 }

  const [, r, g, b, a] = match
  const toHex = (n: string) => parseInt(n, 10).toString(16).padStart(2, '0')
  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`
  const alpha = a ? parseFloat(a) : 1

  return { hex, alpha }
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number): string => {
    const hex = Math.round(Math.max(0, Math.min(255, c))).toString(16)
    return hex.length === 1 ? `0${hex}` : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Muuntaa hex-, rgb- tai rgba-värin ExcelJS ARGB-muotoon (AARRGGBB).
 * Tukee muotoja: #rrggbb, rrggbb, #rrggbbaa, rrggbbaa, rgb(...), rgba(...).
 * Palauttaa undefined, jos väri on invalid tai falsy.
 */
export const colorToArgb = (color?: string): string | undefined => {
  if (!color || typeof color !== 'string') return undefined

  const trimmed = color.trim()

  const hexMatch = trimmed.match(/^#?([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/)
  if (hexMatch) {
    const rgb = hexMatch[1].toUpperCase()
    const alpha = hexMatch[2] ? hexMatch[2].toUpperCase() : 'FF'
    return `${alpha}${rgb}`
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d+(?:\.\d+)?|\.\d+))?\s*\)$/i
  )
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10)
    const g = parseInt(rgbMatch[2], 10)
    const b = parseInt(rgbMatch[3], 10)
    const a = rgbMatch[4]

    if (r > 255 || g > 255 || b > 255 || (a && (parseFloat(a) < 0 || parseFloat(a) > 1))) {
      return undefined
    }

    const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase()
    const alphaHex = a !== undefined ? toHex(Math.round(parseFloat(a) * 255)) : 'FF'

    return `${alphaHex}${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  return undefined
}

export function blendColors(color1Hex: string, color2Hex: string, mode: BlendMode, alpha1 = 1, alpha2 = 1): string {
  // Jos toinen väri on täysin läpinäkyvä, palauta toinen sellaisenaan (ei blend-modia)
  if (alpha1 === 0) return color2Hex
  if (alpha2 === 0) return color1Hex

  // Jos vain toinen väri on osittain läpinäkyvä, älä käytä blend-modia vaan normaali alpha-sekoitus
  if (alpha1 < 1 && alpha2 === 1) {
    const c1 = hexToRgb(color1Hex)
    const c2 = hexToRgb(color2Hex)
    if (!c1 || !c2) return color1Hex
    const result = {
      r: (c1.r * alpha1 + c2.r * (1 - alpha1)) / 255,
      g: (c1.g * alpha1 + c2.g * (1 - alpha1)) / 255,
      b: (c1.b * alpha1 + c2.b * (1 - alpha1)) / 255
    }
    return rgbToHex(result.r * 255, result.g * 255, result.b * 255)
  }

  if (alpha2 < 1 && alpha1 === 1) {
    const c1 = hexToRgb(color1Hex)
    const c2 = hexToRgb(color2Hex)
    if (!c1 || !c2) return color1Hex
    const result = {
      r: (c2.r * alpha2 + c1.r * (1 - alpha2)) / 255,
      g: (c2.g * alpha2 + c1.g * (1 - alpha2)) / 255,
      b: (c2.b * alpha2 + c1.b * (1 - alpha2)) / 255
    }
    return rgbToHex(result.r * 255, result.g * 255, result.b * 255)
  }

  const c1 = hexToRgb(color1Hex)
  const c2 = hexToRgb(color2Hex)
  if (!c1 || !c2) return color1Hex

  // Normalisoi 0-1 välille blend-laskuja varten
  const r1 = c1.r / 255
  const g1 = c1.g / 255
  const b1 = c1.b / 255
  const r2 = c2.r / 255
  const g2 = c2.g / 255
  const b2 = c2.b / 255

  let result: RgbColor

  switch (mode) {
    case 'multiply':
      // Kertoo värit -> tummentaa
      result = {
        r: r1 * r2,
        g: g1 * g2,
        b: b1 * b2
      }
      break

    case 'screen':
      // Käänteinen multiply -> vaalentaa
      result = {
        r: 1 - (1 - r1) * (1 - r2),
        g: 1 - (1 - g1) * (1 - g2),
        b: 1 - (1 - b1) * (1 - b2)
      }
      break

    case 'overlay':
      // Jos pohja < 0.5 -> multiply*2, muuten screen
      result = {
        r: r1 < 0.5 ? 2 * r1 * r2 : 1 - 2 * (1 - r1) * (1 - r2),
        g: g1 < 0.5 ? 2 * g1 * g2 : 1 - 2 * (1 - g1) * (1 - g2),
        b: b1 < 0.5 ? 2 * b1 * b2 : 1 - 2 * (1 - b1) * (1 - b2)
      }
      break

    case 'soft-light': {
      // Pehmeä valo-efekti
      const softLight = (a: number, b: number): number => {
        if (b < 0.5) {
          return 2 * a * b + a * a * (1 - 2 * b)
        }
        return 2 * a * (1 - b) + Math.sqrt(a) * (2 * b - 1)
      }
      result = {
        r: softLight(r1, r2),
        g: softLight(g1, g2),
        b: softLight(b1, b2)
      }
      break
    }

    case 'hard-light':
      // Kova valo - overlay käänteisesti
      result = {
        r: r2 < 0.5 ? 2 * r1 * r2 : 1 - 2 * (1 - r1) * (1 - r2),
        g: g2 < 0.5 ? 2 * g1 * g2 : 1 - 2 * (1 - g1) * (1 - g2),
        b: b2 < 0.5 ? 2 * b1 * b2 : 1 - 2 * (1 - b1) * (1 - b2)
      }
      break

    case 'darken':
      // Valitsee tummemman
      result = {
        r: Math.min(r1, r2),
        g: Math.min(g1, g2),
        b: Math.min(b1, b2)
      }
      break

    case 'lighten':
      // Valitsee vaaleamman
      result = {
        r: Math.max(r1, r2),
        g: Math.max(g1, g2),
        b: Math.max(b1, b2)
      }
      break

    case 'color-dodge': {
      // Väriväistö - voimakas vaalentaminen
      const dodge = (a: number, b: number): number => (b >= 1 ? 1 : Math.min(1, a / (1 - b)))
      result = {
        r: dodge(r1, r2),
        g: dodge(g1, g2),
        b: dodge(b1, b2)
      }
      break
    }

    case 'color-burn': {
      // Väripolttaminen - voimakas tummentaminen
      const burn = (a: number, b: number): number => (b <= 0 ? 0 : Math.max(0, 1 - (1 - a) / b))
      result = {
        r: burn(r1, r2),
        g: burn(g1, g2),
        b: burn(b1, b2)
      }
      break
    }

    case 'difference':
      // Erotus - dramaattinen efekti
      result = {
        r: Math.abs(r1 - r2),
        g: Math.abs(g1 - g2),
        b: Math.abs(b1 - b2)
      }
      break

    case 'exclusion':
      // Pehmeämpi erotus
      result = {
        r: r1 + r2 - 2 * r1 * r2,
        g: g1 + g2 - 2 * g1 * g2,
        b: b1 + b2 - 2 * b1 * b2
      }
      break

    default: // 'normal'
      // Yläpuolinen väri kokonaan
      result = { r: r2, g: g2, b: b2 }
  }

  // Alpha-blending: käytä overlay värin alfaa määrittämään sekoituksen voimakkuus
  const finalAlpha = alpha2
  const finalResult = {
    r: result.r * finalAlpha + r1 * (1 - finalAlpha),
    g: result.g * finalAlpha + g1 * (1 - finalAlpha),
    b: result.b * finalAlpha + b1 * (1 - finalAlpha)
  }

  // Takaisin 0-255 välille ja hex-muotoon
  return rgbToHex(finalResult.r * 255, finalResult.g * 255, finalResult.b * 255)
}

// Funktio PDF-taulukoille
export function getBlendedBackgroundColor(
  baseColor: string | undefined,
  overlayColor: string | undefined,
  mode: BlendMode = 'multiply'
): string {
  // Handle undefined colors
  if (!baseColor && !overlayColor) return 'unset'
  if (!baseColor) return overlayColor!
  if (!overlayColor) return baseColor

  // Jos värit ovat rgba-muodossa, ota alfa huomioon
  const base = rgbaToHex(baseColor)
  const overlay = rgbaToHex(overlayColor)

  const blendedHex = blendColors(base.hex, overlay.hex, mode, base.alpha, overlay.alpha)
  return blendedHex
}
